import { useEffect, useRef } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  Paper,
  CircularProgress,
  Tooltip,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import RefreshIcon from "@mui/icons-material/Refresh";
import Button from "@mui/material/Button";
import { ocrAnalizarImagenes, ocrAnalizarPorRegiones } from "../../../helpers/OcrData";

const OCR_CHUNK_SIZE = 5;
// When analyzing by regions the full image is sent per request — keep batches small to stay under PHP's post_max_size
const OCR_CHUNK_SIZE_REGIONES = 1;

const stripPrefix = (base64) =>
  typeof base64 === "string" && base64.includes(",") ? base64.split(",")[1] : base64;

export const StepOcr = ({ state, dispatch }) => {
  const processingRef = useRef(false);

  useEffect(() => {
    const hasRegiones = state.regiones?.length > 0;
    if (!state.id_ocr_plantilla && !hasRegiones) return;
    if (processingRef.current) return;

    const pendingContenidos = state.contenidos.filter((c) => c.status === "pending");
    if (pendingContenidos.length === 0) return;

    processingRef.current = true;
    runOcr(pendingContenidos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = () => {
    dispatch({ type: "RESET_FAILED_CONTENIDOS" });
    processingRef.current = false;
    const failedAsReset = state.contenidos.filter((c) => c.status === "error")
      .map((c) => ({ ...c, status: "pending" }));
    if (failedAsReset.length > 0) {
      processingRef.current = true;
      runOcr(failedAsReset);
    }
  };

  const handleReanalyzeAll = () => {
    dispatch({ type: "RESET_ALL_CONTENIDOS" });
    processingRef.current = false;
    const all = state.contenidos.map((c) => ({ ...c, status: "pending", ocrResult: null, errorMsg: "" }));
    if (all.length > 0) {
      processingRef.current = true;
      runOcr(all);
    }
  };

  const handleReanalyzeOne = (contenido) => {
    dispatch({ type: "RESET_CONTENIDO", payload: contenido.contenidoId });
    processingRef.current = false;
    processingRef.current = true;
    runOcr([{ ...contenido, status: "pending", ocrResult: null, errorMsg: "" }]);
  };

  const runOcr = async (pendingContenidos) => {
    dispatch({ type: "SET_OCR_LOADING", payload: true });

    const chunkSize = state.regiones?.length > 0 ? OCR_CHUNK_SIZE_REGIONES : OCR_CHUNK_SIZE;
    for (let i = 0; i < pendingContenidos.length; i += chunkSize) {
      const chunk = pendingContenidos.slice(i, i + chunkSize);
      // OCR only on first image of each contenido
      const imagenes = chunk.map((c) => stripPrefix(c.images[0]?.base64));

      try {
        const res = state.regiones?.length > 0
          ? await ocrAnalizarPorRegiones({ imagenes, regiones: state.regiones })
          : await ocrAnalizarImagenes({
              id_plantilla: state.id_ocr_plantilla,
              id_contenidos: null,
              imagenes,
            });

        let resultadosPorImagen = [];
        try {
          const raw = res?.wsResponse;
          if (raw) {
            const parsed = JSON.parse(atob(raw));
            if (Array.isArray(parsed)) {
              resultadosPorImagen = parsed;
            } else {
              resultadosPorImagen = parsed.resultados || [];
            }
          }
        } catch {
          // parse failure: all contenidos in chunk will be marked error
        }

        chunk.forEach((contenido, chunkIdx) => {
          const doc = resultadosPorImagen.find((r) => r.imagen_idx === chunkIdx) || resultadosPorImagen[chunkIdx];
          if (doc && Array.isArray(doc.campos) && doc.campos.length >= 0) {
            dispatch({
              type: "SET_CONTENIDO_OCR_RESULT",
              payload: {
                contenidoId: contenido.contenidoId,
                status: "success",
                ocrResult: doc,
                errorMsg: "",
              },
            });
          } else if (!resultadosPorImagen.length) {
            dispatch({
              type: "SET_CONTENIDO_OCR_RESULT",
              payload: {
                contenidoId: contenido.contenidoId,
                status: "error",
                ocrResult: null,
                errorMsg: "Sin respuesta del servidor OCR",
              },
            });
          } else {
            dispatch({
              type: "SET_CONTENIDO_OCR_RESULT",
              payload: {
                contenidoId: contenido.contenidoId,
                status: "success",
                ocrResult: doc || { campos: [] },
                errorMsg: "",
              },
            });
          }
        });
      } catch (err) {
        chunk.forEach((contenido) => {
          dispatch({
            type: "SET_CONTENIDO_OCR_RESULT",
            payload: {
              contenidoId: contenido.contenidoId,
              status: "error",
              ocrResult: null,
              errorMsg: err?.message || "Error al procesar OCR",
            },
          });
        });
      }
    }

    // Initialize contenido metadata from OCR results
    dispatch({ type: "INIT_CONTENIDO_METADATA" });
    dispatch({ type: "SET_OCR_LOADING", payload: false });
    processingRef.current = false;
  };

  if (!state.id_ocr_plantilla && !(state.regiones?.length > 0)) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600, mx: "auto" }}>
        <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ borderRadius: 2 }}>
          Sin plantilla OCR configurada. Continua al siguiente paso para ingresar los metadatos manualmente.
        </Alert>
      </Box>
    );
  }

  const total = state.contenidos.length;
  const processed = state.contenidos.filter((c) => c.status !== "pending").length;
  const successCount = state.contenidos.filter((c) => c.status === "success").length;
  const errorCount = state.contenidos.filter((c) => c.status === "error").length;
  const progress = total > 0 ? Math.round((processed / total) * 100) : 0;
  const isProcessing = processed < total;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Step header */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#0A395F", mb: 0.5 }}>
            Extraccion OCR
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {state.regiones?.length > 0
              ? `Analizando ${state.regiones.length} region${state.regiones.length !== 1 ? "es" : ""} definidas en cada contenido para extraer los metadatos.`
              : "Analizando los contenidos con la plantilla OCR para extraer los metadatos de forma automatica."}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={handleReanalyzeAll}
          disabled={isProcessing}
          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
          Analizar todo de nuevo
        </Button>
      </Box>

      {/* Progress section */}
      {isProcessing && (
        <Paper
          variant="outlined"
          sx={{ p: 3, mb: 3, borderRadius: 2, textAlign: "center", borderColor: "#90caf9", backgroundColor: "#f8fbff" }}
        >
          <CircularProgress size={48} sx={{ color: "#1565C0", mb: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Procesando contenidos...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {processed} de {total} contenidos analizados
          </Typography>
          <Box sx={{ px: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Progreso</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>{progress}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: "#e3f2fd",
                "& .MuiLinearProgress-bar": {
                  background: "linear-gradient(90deg, #1565C0 0%, #42a5f5 100%)",
                  borderRadius: 4,
                },
              }}
            />
          </Box>
        </Paper>
      )}

      {/* Results summary */}
      {processed === total && (
        <Box sx={{ display: "flex", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
          <Paper
            variant="outlined"
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: 2,
              borderColor: "#a5d6a7",
              backgroundColor: "#f1f8f1",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 20 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#2e7d32", lineHeight: 1 }}>
                {successCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">exitosos</Typography>
            </Box>
          </Paper>
          {errorCount > 0 && (
            <Paper
              variant="outlined"
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: 2,
                borderColor: "#ef9a9a",
                backgroundColor: "#fff5f5",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <ErrorIcon sx={{ color: "#d32f2f", fontSize: 20 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#d32f2f", lineHeight: 1 }}>
                  {errorCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">fallidos</Typography>
              </Box>
            </Paper>
          )}
        </Box>
      )}

      {/* Warning for errors + retry */}
      {errorCount > 0 && processed === total && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button
              size="small"
              startIcon={<ReplayIcon />}
              onClick={handleRetry}
              sx={{ whiteSpace: "nowrap" }}
            >
              Reintentar fallidos
            </Button>
          }
        >
          <strong>{errorCount} contenido{errorCount !== 1 ? "s" : ""} fallaron.</strong>{" "}
          {successCount > 0 ? "Podés continuar con los exitosos o reintentar." : "Reintentá o continuá manualmente."}
        </Alert>
      )}

      {/* Per-contenido result cards */}
      <Grid container spacing={1.5}>
        {state.contenidos.map((contenido, index) => {
          const isPending = contenido.status === "pending";
          const isSuccess = contenido.status === "success";
          const isError = contenido.status === "error";
          const fieldCount = contenido.ocrResult?.campos?.length ?? 0;
          const imageCount = contenido.images?.length ?? 0;
          const firstName = contenido.images?.[0]?.fileName || "";

          return (
            <Grid item key={contenido.contenidoId} xs={12} sm={6} md={4} lg={3}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  borderColor: isPending ? "divider" : isSuccess ? "#a5d6a7" : "#ef9a9a",
                  backgroundColor: isPending ? "#fafafa" : isSuccess ? "#f1f8f1" : "#fff5f5",
                  transition: "all 0.2s",
                }}
              >
                <CardContent sx={{ pb: "12px !important", p: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <Box sx={{ flexShrink: 0, mt: 0.25 }}>
                      {isPending && <HourglassEmptyIcon sx={{ color: "#9e9e9e", fontSize: 18 }} />}
                      {isSuccess && <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 18 }} />}
                      {isError && <ErrorIcon sx={{ color: "#d32f2f", fontSize: 18 }} />}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#0A395F" }}>
                          Contenido #{index + 1}
                        </Typography>
                        <Tooltip title="Analizar con OCR">
                          <span>
                            <IconButton
                              size="small"
                              disabled={isProcessing}
                              onClick={() => handleReanalyzeOne(contenido)}
                              sx={{ p: 0.25 }}
                            >
                              <RefreshIcon sx={{ fontSize: 14, color: isProcessing ? "#bdbdbd" : "#1565C0" }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        title={firstName}
                        sx={{ display: "block" }}
                      >
                        {imageCount} imagen{imageCount !== 1 ? "es" : ""}
                        {firstName ? ` — ${firstName}` : ""}
                      </Typography>

                      {isPending && (
                        <Chip label="Pendiente" size="small" sx={{ mt: 0.5, fontSize: 10, height: 18, color: "#757575", backgroundColor: "#eeeeee" }} />
                      )}
                      {isSuccess && (
                        <Box sx={{ mt: 0.75 }}>
                          <Chip
                            icon={<DocumentScannerIcon style={{ fontSize: 11 }} />}
                            label={`${fieldCount} campo${fieldCount !== 1 ? "s" : ""} extraido${fieldCount !== 1 ? "s" : ""}`}
                            size="small"
                            sx={{ fontSize: 10, height: 20, backgroundColor: "#e8f5e9", color: "#2e7d32" }}
                          />
                          {/* OCR fields preview */}
                          {contenido.ocrResult?.campos?.length > 0 && (
                            <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.25 }}>
                              {contenido.ocrResult.campos.slice(0, 3).map((campo, ci) => (
                                <Box key={ci} sx={{ display: "flex", gap: 0.5, alignItems: "baseline" }}>
                                  <Typography variant="caption" sx={{ fontSize: 10, color: "#546e7a", fontWeight: 600, flexShrink: 0 }}>
                                    {campo.nombre || `Campo ${ci + 1}`}:
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontSize: 10, color: "#1b5e20" }} noWrap title={campo.valor_extraido}>
                                    {campo.valor_extraido || "—"}
                                  </Typography>
                                </Box>
                              ))}
                              {contenido.ocrResult.campos.length > 3 && (
                                <Typography variant="caption" sx={{ fontSize: 10, color: "#78909c" }}>
                                  +{contenido.ocrResult.campos.length - 3} mas...
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      )}
                      {isError && (
                        <Typography variant="caption" sx={{ fontSize: 10, color: "#d32f2f", display: "block", mt: 0.5 }}>
                          {contenido.errorMsg || "Error al procesar"}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
